/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/VideoTutorialModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styles from "./index.module.css";
import VideoDetailsSection from "../../components/VideoTutorialsSection/VideoDetailsSection.jsx";
import {
  createNewVideoTutorial,
  updateExistingVideoTutorial,
} from "../../utils/videoTutorialSubmit.js";

const VideoTutorialModal = ({
  open,
  onClose,
  video,
  sectionId,
  mode = "create",
  onVideoCreated,
  onVideoUpdated,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dialogRef = useRef(null);
  const videoRefRef = useRef(null);
  const isEditMode = mode === "edit";

  const [videoData, setVideoData] = useState({
    name: "",
    description: "",
    videoFile: null,
    videoUrl: null,
    thumbnailSecond: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [originalVideoData, setOriginalVideoData] = useState(null);

  useEffect(() => {
    if (open && isEditMode && video) {
      const initialVideoData = {
        _id: video._id || video.id,
        name: video.name || "",
        description: video.description || "",
        videoFile: null,
        videoUrl: video.videoUrl || null,
        thumbnailSecond:
          video.thumbnailSecond !== undefined ? video.thumbnailSecond : 0,
      };
      setVideoData(initialVideoData);
      setOriginalVideoData(JSON.parse(JSON.stringify(initialVideoData)));
      setError("");
    } else if (open && !isEditMode) {
      setVideoData({
        name: "",
        description: "",
        videoFile: null,
        videoUrl: null,
        thumbnailSecond: 0,
      });
      setOriginalVideoData(null);
      setError("");
    }
  }, [open, isEditMode, video]);

  useEffect(() => {
    const handleClose = () => {
      setOriginalVideoData(null);
      onClose();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open) {
      dialog.showModal();
    } else {
      if (dialog) {
        dialog.close();
      }
      // Pause video when modal closes
      if (videoRefRef.current && videoRefRef.current.current) {
        videoRefRef.current.current.pause();
        videoRefRef.current.current.currentTime = 0;
      }
      setOriginalVideoData(null);
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [open, onClose]);

  const handleVideoChange = (index, updatedVideo) => {
    setVideoData(updatedVideo);
  };

  const handleVideoRef = (ref) => {
    videoRefRef.current = ref;
  };

  const pauseVideo = () => {
    if (videoRefRef.current && videoRefRef.current.current) {
      videoRefRef.current.current.pause();
      videoRefRef.current.current.currentTime = 0;
    }
  };

  const handleCancel = () => {
    pauseVideo();
    setOriginalVideoData(null);
    onClose();
  };

  const hasChanges = () => {
    if (!isEditMode) {
      return (
        videoData.name?.trim() ||
        videoData.description?.trim() ||
        videoData.videoFile ||
        videoData.thumbnailSecond !== 0
      );
    }

    if (!originalVideoData) {
      return false;
    }

    // Check if name changed
    if (videoData.name?.trim() !== originalVideoData.name?.trim()) {
      return true;
    }

    // Check if description changed
    if (
      videoData.description?.trim() !== originalVideoData.description?.trim()
    ) {
      return true;
    }

    // Check if thumbnailSecond changed
    if (
      (videoData.thumbnailSecond || 0) !==
      (originalVideoData.thumbnailSecond || 0)
    ) {
      return true;
    }

    // Check if a new video file was uploaded
    if (videoData.videoFile) {
      return true;
    }

    return false;
  };

  const isFormValid = () => {
    return (
      videoData.name?.trim() &&
      videoData.description?.trim() &&
      (videoData.videoFile || videoData.videoUrl) &&
      videoData.thumbnailSecond >= 0
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      return;
    }

    if (!sectionId) {
      setError(t("components.videoTutorialModal.sectionIdRequired"));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const videoId = videoData._id || videoData.id;
      let outcome;

      if (videoId && videoData.videoFile) {
        outcome = await updateExistingVideoTutorial({
          videoId,
          videoData,
          sectionId,
          navigate,
          withNewFile: true,
        });
        if (outcome.ok && onVideoUpdated) {
          onVideoUpdated();
        }
      } else if (videoId) {
        outcome = await updateExistingVideoTutorial({
          videoId,
          videoData,
          sectionId,
          navigate,
          withNewFile: false,
        });
        if (outcome.ok && onVideoUpdated) {
          onVideoUpdated();
        }
      } else {
        outcome = await createNewVideoTutorial({
          videoData,
          sectionId,
          navigate,
        });
        if (outcome.missingFile) {
          setError(t("components.videoTutorialModal.videoFileRequired"));
          return;
        }
        if (outcome.ok && onVideoCreated) {
          onVideoCreated();
        }
      }

      if (!outcome?.ok) {
        setError(
          videoId
            ? t("components.videoTutorialModal.updateError")
            : t("components.videoTutorialModal.createError"),
        );
        return;
      }

      onClose();
    } catch (error) {
      logger.error("Error saving video:", { err: sanitizeError(error) });
      const isUploadError = error?.message?.includes("Failed to upload video");
      setError(
        isUploadError
          ? t("components.videoTutorialModal.uploadError")
          : isEditMode
            ? t("components.videoTutorialModal.updateError")
            : t("components.videoTutorialModal.createError"),
      );
    } finally {
      setLoading(false);
    }
  };

  const getModalTitle = () => {
    return isEditMode
      ? t("components.videoTutorialModal.editTitle")
      : t("components.videoTutorialModal.createTitle");
  };

  const getSubtitle = () => {
    return isEditMode
      ? t("components.videoTutorialModal.editSubtitle")
      : t("components.videoTutorialModal.subtitle");
  };

  return (
    <>
      {open && <div className={styles.overlay}></div>}
      <dialog ref={dialogRef} className={styles.modal}>
        <div className={styles.closeButtonContainer}>
          <button className={styles.closeButton} onClick={handleCancel}>
            &times;
          </button>
        </div>

        <div className={styles.modalContent}>
          <label className={styles.modalTitle}>{getModalTitle()}</label>
          <p className={styles.modalSubtitle}>{getSubtitle()}</p>

          {error && <div className={styles.errorMessage}>{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className={styles.formContainer}>
              <VideoDetailsSection
                video={videoData}
                index={0}
                onVideoChange={handleVideoChange}
                onRemove={() => {}}
                showRemoveButton={false}
                onVideoRef={handleVideoRef}
              />
            </div>

            {/* Modal Buttons */}
            <div className={styles.modalButtons}>
              <button
                type="button"
                onClick={handleCancel}
                className={styles.cancelButton}
                disabled={loading}
              >
                {t("components.videoTutorialModal.cancelButton")}
              </button>
              <button
                type="submit"
                className={styles.saveButton}
                disabled={
                  loading || !isFormValid() || (isEditMode && !hasChanges())
                }
              >
                {loading
                  ? t("components.videoTutorialModal.saving")
                  : t("components.videoTutorialModal.saveButton")}
              </button>
            </div>
          </form>
        </div>
      </dialog>
    </>
  );
};

export default VideoTutorialModal;
